import React, {useCallback, useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getIsMenuOpen} from "modules/selectors/common";
import {
	disableBodyScroll,
	getClassAccordingBoolean,
	handleMenuAnalyticsClick,
} from "modules/utils/helpers";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {setIsTutorialViewed, toggleMenu, toggleTutorialModal} from "modules/actions";
import {DebugHandler, Exists} from "components";
import {useIsMobile} from "modules/utils/hooks";
import logoAsset from "assets/images/logos/logoWhite.png";
import {AdobeAnalyticService} from "modules/utils/Services/AdobeAnalyticService";
import {AdobeTrackStateActions, MenuSection} from "modules/enums";
// import {getContestExternalId} from "modules/selectors";
import {NavLink, useNavigate} from "react-router-dom";
import {getCurrentUserId, getIsSummaryShouldBeShown} from "modules/selectors";

const MenuWrapper = styled.div`
	position: fixed;
	top: 66px; //Header height;
	left: 0;
	right: 0;
	bottom: 0;
	background: ${({theme}) => theme.header.menu.background};
	z-index: 30;
	transform: translateX(-100%);
	transition: 0.5s;
	overflow: hidden;
	display: flex;
	justify-content: flex-start;
	align-items: center;
	flex-direction: column;
	gap: 12px;

	&.open {
		transform: translateX(0);
	}

	a {
		font-family: ${({theme}) => theme.fonts.secondary};
		font-style: normal;
		font-weight: 500;
		font-size: 20px;
		line-height: 150%;
		color: ${({theme}) => theme.colors.text.white};
		display: block;
		width: 100%;
		text-align: center;
		transition: 0.15s;

		&:hover,
		&:active,
		&:focus,
		&.active {
			cursor: pointer;
			color: ${({theme}) => theme.colors.brand.primary};
		}
	}
`;

const MenuLink = styled.a`
	font-family: ${({theme}) => theme.fonts.secondary};
	font-style: normal;
	font-weight: 500;
	font-size: 20px;
	line-height: 150%;
	color: ${({theme}) => theme.colors.text.white};
	display: block;
	width: 100%;
	text-align: center;
	transition: 0.15s;

	&:hover,
	&:active,
	&:focus,
	&.active {
		cursor: pointer;
		color: ${({theme}) => theme.colors.brand.primary};
	}
`;

const MobileLogos = styled.div`
	height: 52px;
	width: 100%;
	display: flex;
	justify-content: space-between;
	align-items: center;
`;

const LogoLink = styled(NavLink)`
	margin-right: auto;
	padding-right: 80px;
	display: flex;
	height: 100%;
	img {
		height: 100%;
	}
`;

export const MobileMenu: React.FC = () => {
	const dispatch = useDispatch();
	const {t} = useTranslation();
	const isOpen = useSelector(getIsMenuOpen);
	const className = getClassAccordingBoolean("open", isOpen);
	const {isMobile} = useIsMobile("(max-width: 720px)");
	const navigate = useNavigate();
	const userId = useSelector(getCurrentUserId);
	const homeUrl = useSelector(getIsSummaryShouldBeShown) ? "/summary" : "";
	// const mlbContestId = useSelector(getContestExternalId);

	useEffect(() => {
		if (isOpen) {
			AdobeAnalyticService.TrackState({
				"page.name": AdobeTrackStateActions.Menu,
				"page.viewType": "",
				"page.viewSubType": "",
			});
		}
	}, [isOpen]);

	useEffect(() => {
		disableBodyScroll(isOpen);
	}, [isOpen]);

	const openTutorial = useCallback(() => {
		dispatch(toggleMenu(false));
		dispatch(setIsTutorialViewed(true));
		dispatch(toggleTutorialModal(true));
		handleMenuAnalyticsClick(MenuSection.HowToPlay);
	}, [dispatch]);

	// const leaderboardHandler = useCallback(() => {
	// 	handleMenuAnalyticsClick(MenuSection.Leaderboards);
	// 	openLeaderboardsNative(mlbContestId);
	// }, [mlbContestId]);

	// const feedBackHandler = useCallback(() => {
	// 	handleMenuAnalyticsClick(MenuSection.Feedback, FEEDBACK_URL);
	// 	openExternalURL(FEEDBACK_URL);
	// }, []);

	const openProfile = () => {
		if (userId) {
			navigate(`/leaderboards/${userId}`);
			dispatch(toggleMenu(false));
		}
	};

	const openLeaderboard = () => {
		navigate("/leaderboards");
		dispatch(toggleMenu(false));
	};

	return (
		<React.Fragment>
			<Exists when={isMobile}>
				<MobileLogos>
					<LogoLink to={homeUrl}>
						<img src={logoAsset} alt="MLB BTN" />
					</LogoLink>
				</MobileLogos>
			</Exists>

			<MenuWrapper className={className}>
				<MenuLink onClick={openTutorial}>{t("header.menu.tutorial")}</MenuLink>
				{/*<MenuLink onClick={leaderboardHandler}>{t("header.menu.leaderboards")}</MenuLink>*/}
				<MenuLink onClick={openLeaderboard}>{t("header.menu.leaderboards")}</MenuLink>
				<MenuLink onClick={openProfile}>
					{t("header.menu.user_profile", "User profile")}
				</MenuLink>
				{/*<MenuLink onClick={feedBackHandler}>{t("header.menu.feedback")}</MenuLink>*/}
				<Exists when={isOpen}>
					<DebugHandler />
				</Exists>
			</MenuWrapper>
		</React.Fragment>
	);
};
